/**
 * <CondominioScrub />
 * ---------------------------------------------------------------------------
 * Seção do condomínio/amenidades controlada por SCROLL (scroll-scrub).
 *
 * Um único vídeo cinematográfico (~45s, 720×1280) fica PAUSADO e o `currentTime`
 * avança conforme o user rola a página. Cada amenidade é uma "cena" do vídeo.
 *
 * TRANSIÇÃO (SPEC 62 — pedido do user): o primeiro frame do vídeo É a MESMA
 * imagem do último showcase das plantas (living social + terraço,
 * 07-real-living-garden-916.jpg). Em vez do vídeo "subir" deslizando e já tocar,
 * a entrada tem 2 fases:
 *   FASE 1 (fill): a imagem entra no MESMO enquadramento que tinha no feed
 *     (faixa de baixo, abaixo do header das plantas) e CRESCE até preencher a
 *     tela inteira. currentTime fica travado em 0 (= foto estática do living).
 *   FASE 2 (scrub): só depois de preencher a tela é que o vídeo começa a rodar
 *     — o resto do scroll vira currentTime (0 → duração).
 * Como a foto e o frame 0 são idênticos, a fase 1 é uma continuação perfeita.
 *
 * Mecânica = padrão /bio (BioPage.jsx): Section alta cria "scroll budget", um
 * <Sticky> fixa o canvas no viewport, e o progresso do scroll dentro da Section
 * vira fill+currentTime. Atualizações por-frame (top do wrap, barra, legenda)
 * são imperativas (via ref) pra não re-renderizar o React a cada scroll.
 *
 * ⚠ GOTCHAS iOS Safari (a 1ª tentativa FALHOU no iPhone real):
 * 1. iOS REJEITA `video.currentTime = X` antes do `canplay` → só seekamos após
 *    `ready`. Antes disso o poster (mesma foto) cobre.
 * 2. Poster = foto do living → zero flash branco/preto e transição fiel.
 * 3. `src` com `#t=0.001` força decode do 1º frame.
 * 4. Atributos: muted + defaultMuted(via ref) + playsInline + webkit-playsinline
 *    + x5-playsinline + preload + disablePiP + disableRemotePlayback. SEM autoPlay.
 * 5. Sticky + dvh quebra em iOS < 16 → min/max-height: 100dvh + fallback 100vh.
 * 6. iOS não dispara `wheel` → só `scroll` (via getBoundingClientRect).
 * 7. Warm-up play()→pause() no canplay prima o decoder pros seeks renderizarem.
 *
 * Debug remoto: `?debug=1` mostra readyState/currentTime/fill/scrub no rodapé.
 */
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

// Pace do scroll: px de rolagem por segundo de vídeo (fase scrub). Ajustável.
const PX_PER_SECOND = 200;
// Comprimento da fase de "fill" (a imagem crescendo até preencher), em fração
// da altura da tela de scroll. SPEC 64 #C: encurtado de 0.9 → 0.55 — o reveal
// "fecha a tela" com ~meia rolada em vez de quase uma tela inteira, então a
// imagem para de ficar "travada" (currentTime preso em 0) tão cedo e o header
// das plantas sai rápido. Tunável no dedo.
const FILL_FRACTION = 0.55;
// Enquadramento inicial: de onde a imagem é revelada. 0.322 = a foto começa
// ocupando os ~68% de baixo da tela (medido: top do card do feed = 0.322·innerH,
// abaixo do header das plantas). A revelação descobre dali até a tela toda.
const FILL_START_TOP_FRACTION = 0.322;
// SPEC 64 #1 (finalzinho): o clip-path TERMINA de fechar o topo já em 85% do
// fill (não em 100%). Sobra um "settle" onde a sala fica revelada inteira,
// estática, ANTES do scrub — mata a faixa navy de borda dura que sobrava no topo
// quando o user parava de rolar perto do fim. O crossover (fillP=0) não muda.
const FILL_CLOSE_AT = 0.85;
// "Hold": zona de scroll DEPOIS do fill e ANTES do scrub onde a sala aparece
// cheia, parada e com o topo 100% fechado. Dá um ponto de descanso confortável
// ("expande → fecha a tela → segura → começa o vídeo"). Curto de propósito pra
// não reintroduzir a sensação de "travada". Em fração da tela.
const FILL_HOLD_FRACTION = 0.12;

const VIDEO_SRC = '/sofia/condominio/sofia-condominio-sequence.mp4';
const POSTER_SRC = '/sofia/07-real-living-garden-916.jpg';
const FALLBACK_DURATION = 45.4; // 45.375s real (ffprobe); fallback até loadedmetadata

// Ranges de currentTime → nome da cena exibido no overlay (ver SPEC 62).
const SCENES = [
  { from: 0,    to: 5,    eyebrow: 'Áreas comuns', name: 'Conheça o condomínio' },
  { from: 5,    to: 13,   eyebrow: 'Esporte',      name: 'Quadra de tênis' },
  { from: 13,   to: 21,   eyebrow: 'Lazer',        name: 'Piscinas' },
  { from: 21,   to: 26,   eyebrow: 'Bem-estar',    name: 'Spa' },
  { from: 26,   to: 31,   eyebrow: 'Wellness',     name: 'Academia' },
  { from: 31,   to: 39,   eyebrow: 'Convívio',     name: 'Recreação' },
  // tail sem texto — evita o "resquício" de Sofia, à noite; só o CTA aparece aqui
  { from: 39,   to: 46,   eyebrow: '',             name: '' },
];

const sceneAt = (t) => {
  for (let i = SCENES.length - 1; i >= 0; i--) {
    if (t >= SCENES[i].from) return i;
  }
  return 0;
};

const clamp01 = (x) => (x < 0 ? 0 : x > 1 ? 1 : x);

// CTA do take final (substitui "Sofia, à noite"). Textos fáceis de trocar.
const CTA_START = 0.86; // scrubP onde o CTA começa a aparecer (fim do vídeo)
const CTA = {
  eyebrow: 'Sofia · Campo Belo',
  headline: 'Conheça o Sofia',
  sub: '', // sem subtexto
  button: 'Quero mais informações',
};

const Section = styled.section`
  position: relative;
  width: 100%;
  background: #0a070b;
`;

// Canvas FIXO (não desliza) cobrindo o viewport. Visibilidade é gateada por
// scroll (só aparece quando a seção entra). Fundo TRANSPARENTE de propósito:
// durante o fill, a faixa acima do vídeo deixa ver o que está atrás (o header
// das plantas) em vez de uma faixa preta → a imagem do living "cresce por cima"
// sem corte. pointer-events:none → nunca bloqueia interação por baixo.
const Fixed = styled.div`
  position: fixed;
  inset: 0;
  height: 100dvh;
  overflow: hidden;
  background: transparent;
  pointer-events: none;
  visibility: hidden;

  @supports not (height: 100dvh) {
    height: 100vh;
  }
`;

// Wrap full-bleed: o vídeo fica SEMPRE no tamanho final de tela cheia (object-fit
// cover, crop fixo). A "expansão" é uma REVELAÇÃO via clip-path inset — descobre
// da faixa de baixo (= rect da foto no feed) até a tela toda, SEM reframe (a
// técnica das LPs Apple). clip-path é setado imperativamente a cada frame.
const VideoWrap = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  background: #0a070b;
  z-index: 0;
  will-change: clip-path;
`;

const Media = styled.div`
  position: absolute;
  inset: 0;
  & > video,
  & > img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    background: #0a070b;
  }
  & > video {
    pointer-events: none;
  }
`;

const Poster = styled.img`
  z-index: 1;
  transition: opacity 350ms ease;
  opacity: ${({ $hidden }) => ($hidden ? 0 : 1)};
`;

const Scrim = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  pointer-events: none;
  background: linear-gradient(
    180deg,
    rgba(10, 7, 11, 0.3) 0%,
    rgba(10, 7, 11, 0) 22%,
    rgba(10, 7, 11, 0) 58%,
    rgba(10, 7, 11, 0.55) 84%,
    rgba(10, 7, 11, 0.92) 100%
  );
`;

const Progress = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 2px;
  width: 0%;
  z-index: 4;
  background: linear-gradient(90deg, #e9c87a 0%, #fad78c 100%);
  box-shadow: 0 0 10px rgba(233, 200, 122, 0.55);
`;

const Caption = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 3;
  text-align: center;
  /* bottom extra reserva espaço pro botão flutuante não tampar a legenda da cena. */
  padding: 0 24px 92px;
  pointer-events: none;
  opacity: 0;
  transform: translateY(14px);

  @media (min-width: 768px) {
    padding-bottom: 108px;
  }
`;

const Eyebrow = styled.div`
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 10px;
  font-weight: 400;
  letter-spacing: 0.42em;
  text-transform: uppercase;
  color: #e9c87a;
  opacity: 0.9;
  margin-bottom: 14px;

  @media (min-width: 768px) {
    font-size: 12px;
  }
`;

const SceneName = styled.div`
  font-family: 'Playfair Display', 'Cormorant Garamond', Georgia, serif;
  font-style: italic;
  font-weight: 400;
  font-size: clamp(38px, 11vw, 88px);
  line-height: 0.95;
  color: #faf3e0;
  text-shadow: 0 2px 40px rgba(0, 0, 0, 0.6);
`;

// Bloco CTA do take final: texto destacado + botão JUNTOS, centralizado mais
// pra cima (lower-center). opacity/transform setados imperativamente por ctaP.
const FinalCta = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 16%;
  z-index: 5;
  text-align: center;
  padding: 0 26px;
  pointer-events: none; /* só o botão reativa */
  opacity: 0;
  transform: translateY(18px);

  @media (min-width: 768px) {
    bottom: 18%;
  }
`;

const CtaEyebrow = styled.div`
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 10px;
  letter-spacing: 0.42em;
  text-transform: uppercase;
  color: #e9c87a;
  opacity: 0.95;
  margin-bottom: 14px;
  @media (min-width: 768px) { font-size: 12px; }
`;

const CtaHeadline = styled.div`
  font-family: 'Playfair Display', 'Cormorant Garamond', Georgia, serif;
  font-style: italic;
  font-weight: 400;
  font-size: clamp(36px, 10vw, 76px);
  line-height: 1;
  color: #faf3e0;
  text-shadow: 0 2px 40px rgba(0, 0, 0, 0.7), 0 0 36px rgba(233, 200, 122, 0.18);
  margin: 0 0 12px;
`;

const CtaSub = styled.div`
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 13px;
  line-height: 1.5;
  letter-spacing: 0.02em;
  color: rgba(245, 232, 200, 0.82);
  margin: 0 auto 24px;
  max-width: 24em;
  text-shadow: 0 2px 12px rgba(0, 0, 0, 0.6);
`;

const CtaButton = styled.button`
  pointer-events: auto;
  border: none;
  cursor: pointer;
  padding: 15px 40px;
  border-radius: 999px;
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: #2a1d0f;
  background: linear-gradient(135deg, #f3d99a 0%, #e9c87a 48%, #d8b063 100%);
  box-shadow:
    0 10px 30px rgba(0, 0, 0, 0.5),
    0 0 0 1px rgba(250, 243, 224, 0.3) inset,
    0 0 26px rgba(233, 200, 122, 0.4);
  transition: transform 160ms ease;
  &:active { transform: scale(0.97); }
`;

const DebugPanel = styled.div`
  position: fixed;
  bottom: 8px;
  left: 8px;
  right: 8px;
  z-index: 9999;
  padding: 8px 10px;
  background: rgba(0, 0, 0, 0.85);
  color: #0f0;
  font: 10px/1.3 ui-monospace, Menlo, monospace;
  border-radius: 6px;
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 38vh;
  overflow: auto;
  pointer-events: none;
`;

export default function CondominioScrub() {
  const sectionRef = useRef(null);
  const fixedRef = useRef(null);
  const videoRef = useRef(null);
  const wrapRef = useRef(null);
  const progressRef = useRef(null);
  const captionRef = useRef(null);
  const finalCtaRef = useRef(null);
  const ctaVisibleRef = useRef(false);

  const [duration, setDuration] = useState(FALLBACK_DURATION);
  const [ready, setReady] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [sceneIndex, setSceneIndex] = useState(0);
  const [debug, setDebug] = useState({});

  const readyRef = useRef(false);
  const durationRef = useRef(FALLBACK_DURATION);
  const sceneRef = useRef(0);
  const lastDebugRef = useRef(0);

  const debugEnabled =
    typeof window !== 'undefined' && window.location.search.includes('debug=1');

  // Altura da Section = fase fill + budget do scrub + 1 viewport (saída sticky).
  const [vh, setVh] = useState(
    typeof window !== 'undefined' ? window.innerHeight : 800
  );
  useEffect(() => {
    const onResize = () => setVh(window.innerHeight);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const fillPx = Math.round(vh * FILL_FRACTION);
  const holdPx = Math.round(vh * FILL_HOLD_FRACTION);
  const sectionHeight = Math.round(fillPx + holdPx + duration * PX_PER_SECOND + vh);

  // Lazy-load: baixa o vídeo ~1.5 viewport antes (poster cobre se atrasar).
  useEffect(() => {
    if (shouldLoad) return;
    const sec = sectionRef.current;
    if (!sec) return;
    const check = () => {
      const rect = sec.getBoundingClientRect();
      if (rect.top < window.innerHeight * 1.5) {
        setShouldLoad(true);
        return true;
      }
      return false;
    };
    if (check()) return;
    const onScroll = () => {
      if (check()) window.removeEventListener('scroll', onScroll);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [shouldLoad]);

  // Eventos do vídeo: duração real + canplay (libera seek) + warm-up do decoder.
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !shouldLoad) return;

    v.muted = true;
    v.defaultMuted = true;

    const log = (patch) =>
      setDebug((s) => ({ ...s, ...patch, ts: Math.round(performance.now()) }));

    const onLoaded = () => {
      if (v.duration && isFinite(v.duration)) {
        durationRef.current = v.duration;
        setDuration(v.duration);
      }
      log({ event: 'loadedmetadata', duration: v.duration, readyState: v.readyState });
    };
    const onCanPlay = () => {
      readyRef.current = true;
      setReady(true);
      log({ event: 'canplay', readyState: v.readyState });
      const p = v.play();
      if (p && typeof p.then === 'function') {
        p.then(() => {
          v.pause();
          log({ event: 'warmup-ok' });
          seekToScroll();
        }).catch((err) => {
          log({ event: 'warmup-blocked', err: err?.name });
          seekToScroll();
        });
      } else {
        seekToScroll();
      }
    };
    const onError = () =>
      log({ event: 'error', code: v.error?.code, msg: v.error?.message });

    v.addEventListener('loadedmetadata', onLoaded);
    v.addEventListener('canplay', onCanPlay);
    v.addEventListener('error', onError);
    log({ mounted: true, ua: navigator.userAgent.slice(0, 48) });
    return () => {
      v.removeEventListener('loadedmetadata', onLoaded);
      v.removeEventListener('canplay', onCanPlay);
      v.removeEventListener('error', onError);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldLoad]);

  // Núcleo: scroll → fill (cresce a imagem) → scrub (currentTime). rAF-coalesced.
  const rafRef = useRef(0);
  const seekToScroll = () => {
    const sec = sectionRef.current;
    const v = videoRef.current;
    if (!sec) return;
    const rect = sec.getBoundingClientRect();
    const innerH = window.innerHeight;

    // Canvas FIXO visível só enquanto a seção sobrepõe o viewport — fora disso
    // ele cobriria a tela inteira (é position:fixed). Aparece exatamente quando
    // o topo da seção cruza o fundo do viewport = ponto de liberação do feed.
    const active = rect.top < innerH && rect.bottom > 0;
    if (fixedRef.current) {
      fixedRef.current.style.visibility = active ? 'visible' : 'hidden';
    }

    // enter = quanto já passou do ponto de entrada (rect.top == innerH → enter 0).
    // Sem position:sticky não há "deslize" — só o wrap cresce (sem faixa preta).
    const enter = innerH - rect.top;
    const dur = durationRef.current || FALLBACK_DURATION;
    const fillBudget = Math.max(1, Math.round(innerH * FILL_FRACTION));
    const holdBudget = Math.max(0, Math.round(innerH * FILL_HOLD_FRACTION));
    const scrubBudget = Math.max(1, Math.round(dur * PX_PER_SECOND));
    const fillP = clamp01(enter / fillBudget);
    // clipP fecha o topo ANTES do fim do fill (em FILL_CLOSE_AT do budget). Depois
    // disso a sala fica revelada inteira e ESTÁTICA durante o resto do fill + o
    // hold, antes do scrub. Mata a faixa navy de borda dura que sobrava no topo
    // quando o user parava de rolar perto do fim do reveal (SPEC 64 #1 finalzinho).
    const clipP = clamp01(enter / (fillBudget * FILL_CLOSE_AT));
    // scrub (currentTime avança) só começa depois do fill + hold.
    const scrubP = clamp01((enter - fillBudget - holdBudget) / scrubBudget);
    // Capa o alvo um tiquinho antes da duração: seekar pro fim EXATO faz o vídeo
    // resetar pra 0, e o guard `abs > 0.04` re-seeka pro fim → flicker frenético
    // entre o 1º e o último frame no fundo do scroll. dur-0.12 segura no último
    // frame sem o reset.
    const t = Math.min(scrubP * dur, Math.max(0, dur - 0.12));

    // FASE 1 (fill): REVELA a imagem (já em tela cheia) da faixa de baixo (=
    // rect da foto no feed) até a tela toda, descobrindo o topo. clip-path inset
    // não re-cropa (crop fixo de cover) → expansão suave sem reframe nem faixa
    // preta (topo descoberto deixa ver o header das plantas atrás).
    if (wrapRef.current) {
      const insetTop = Math.round((1 - clipP) * innerH * FILL_START_TOP_FRACTION);
      const clip = `inset(${insetTop}px 0 0 0)`;
      wrapRef.current.style.clipPath = clip;
      wrapRef.current.style.webkitClipPath = clip;
    }
    // CTA final: no fim do scrub a legenda da cena vira um bloco CTA destacado
    // (texto + botão), e o botão flutuante some pra não duplicar.
    const ctaP = clamp01((scrubP - CTA_START) / (1 - CTA_START));

    // legenda da cena: aparece quando o reveal fechou (clipP>=1) e some no CTA.
    if (captionRef.current) {
      const o = (scrubP > 0 || clipP >= 1 ? 1 : 0) * (1 - ctaP);
      captionRef.current.style.opacity = String(o);
      captionRef.current.style.transform = `translateY(${(1 - o) * 14}px)`;
    }
    if (finalCtaRef.current) {
      finalCtaRef.current.style.opacity = String(ctaP);
      finalCtaRef.current.style.transform = `translateY(${(1 - ctaP) * 18}px)`;
    }
    // avisa o botão flutuante pra sumir/voltar (só no cruzar do limite)
    const ctaVisible = ctaP > 0.06;
    if (ctaVisible !== ctaVisibleRef.current) {
      ctaVisibleRef.current = ctaVisible;
      window.dispatchEvent(new CustomEvent('sofia-cta-visible', { detail: ctaVisible }));
    }
    if (progressRef.current) {
      progressRef.current.style.width = `${(scrubP * 100).toFixed(2)}%`;
    }

    // cena (só muda state quando o índice muda → re-render raro)
    const idx = sceneAt(t);
    if (idx !== sceneRef.current) {
      sceneRef.current = idx;
      setSceneIndex(idx);
    }

    // FASE 2 (scrub): seek só após canplay (gotcha iOS #1).
    if (v && readyRef.current && Math.abs(v.currentTime - t) > 0.04) {
      try {
        v.currentTime = t;
      } catch (_) {
        /* iOS pré-canplay rejeita — readyRef já protege */
      }
    }

    if (debugEnabled) {
      const now = performance.now();
      if (now - lastDebugRef.current > 200) {
        lastDebugRef.current = now;
        setDebug((s) => ({
          ...s,
          fillP: fillP.toFixed(2),
          clipP: clipP.toFixed(2),
          insetTop: Math.round((1 - clipP) * innerH * FILL_START_TOP_FRACTION),
          scrubP: scrubP.toFixed(2),
          target: t.toFixed(2),
          currentTime: v ? v.currentTime.toFixed(2) : null,
          paused: v ? v.paused : null,
          readyState: v ? v.readyState : null,
          scene: SCENES[idx]?.name,
        }));
      }
    }
  };

  useEffect(() => {
    const onScroll = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = 0;
        seekToScroll();
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    seekToScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, duration, vh]);

  const scene = SCENES[sceneIndex] || SCENES[0];

  return (
    <Section ref={sectionRef} style={{ height: `${sectionHeight}px` }}>
      <Fixed ref={fixedRef}>
        {/* VideoWrap cresce da faixa de baixo (rect do feed) até a tela toda.
            Scrim + legenda ficam DENTRO dele pra acompanhar a expansão. O topo
            da Fixed fica transparente durante o fill → mostra o que está atrás
            (header das plantas), nunca uma faixa preta. */}
        <VideoWrap
          ref={wrapRef}
          style={{
            clipPath: `inset(${Math.round(vh * FILL_START_TOP_FRACTION)}px 0 0 0)`,
            WebkitClipPath: `inset(${Math.round(vh * FILL_START_TOP_FRACTION)}px 0 0 0)`,
          }}
        >
          <Media>
            {shouldLoad && (
              <video
                ref={videoRef}
                muted
                playsInline
                webkit-playsinline="true"
                x5-playsinline="true"
                preload="auto"
                poster={POSTER_SRC}
                src={`${VIDEO_SRC}#t=0.001`}
                disablePictureInPicture
                disableRemotePlayback
                controls={false}
              />
            )}
            <Poster src={POSTER_SRC} alt="" aria-hidden="true" $hidden={ready} />
          </Media>
          <Scrim />
          <Caption ref={captionRef}>
            <Eyebrow>{scene.eyebrow}</Eyebrow>
            <SceneName>{scene.name}</SceneName>
          </Caption>
        </VideoWrap>
        <Progress ref={progressRef} />
        <FinalCta ref={finalCtaRef}>
          <CtaEyebrow>{CTA.eyebrow}</CtaEyebrow>
          <CtaHeadline>{CTA.headline}</CtaHeadline>
          {CTA.sub && <CtaSub>{CTA.sub}</CtaSub>}
          <CtaButton
            onClick={() => window.dispatchEvent(new CustomEvent('sofia-open-agendar'))}
          >
            {CTA.button}
          </CtaButton>
        </FinalCta>
      </Fixed>
      {debugEnabled && (
        <DebugPanel>
          {`shouldLoad=${shouldLoad} ready=${ready}\n` +
            JSON.stringify(debug, null, 2)}
        </DebugPanel>
      )}
    </Section>
  );
}
